import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppearance } from "../../contexts/AppearanceContext";
import {
  DollarSign, TrendingUp, TrendingDown, Target, Clock, AlertTriangle, Trophy,
  ThumbsDown, Users, Loader2, Download, CheckSquare, MessageCircle, FileSignature,
  Zap, BarChart2, Info, AlertCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer,
  LineChart, Line, Cell, Legend,
} from "recharts";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtK = (v) => `R$ ${(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

function KpiCard({ icon: Icon, title, value, sub, color, prev, unit = "" }) {
  const diff = prev != null && prev !== undefined ? ((value || 0) - prev) : null;
  const pct = prev && prev > 0 ? Math.round(((value || 0) - prev) / prev * 100) : null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "18" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
      </div>
      <p className="text-2xl font-bold text-slate-900">{typeof value === "number" && unit === "R$" ? fmtK(value) : `${value ?? "—"}${unit}`}</p>
      <div className="flex items-center gap-2 mt-0.5">
        {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
        {pct !== null && (
          <span className={`text-[10px] font-semibold ${pct >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
            {pct >= 0 ? "+" : ""}{pct}% vs anterior
          </span>
        )}
      </div>
    </div>
  );
}

export default function ExecutiveReport({ filterHook }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wonTableFilter, setWonTableFilter] = useState("all");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = filterHook.buildApiFilters({
        period: { start: filterHook.filters.periodStart + "T00:00:00Z", end: filterHook.filters.periodEnd + "T23:59:59Z", field: "created_at" },
      });
      const res = await axios.post(`${API}/reports/executive`, { filters }, { withCredentials: true });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [filterHook]);

  useEffect(() => { fetchData(); }, [filterHook.filters.periodStart, filterHook.filters.periodEnd, filterHook.filters.pipelineIds, filterHook.filters.responsavelIds]); // eslint-disable-line

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data.team?.length) {
      const ws = XLSX.utils.json_to_sheet(data.team.map(t => ({
        "Vendedor": t.name, "MRR Ganho": t.mrr_ganho, "Deals Ganhos": t.deals_ganhos,
        "Deals Perdidos": t.deals_perdidos, "Conversão %": t.taxa_conversao,
        "Tarefas Concluídas": t.tarefas_concluidas, "WhatsApp": t.whatsapp,
      })));
      XLSX.utils.book_append_sheet(wb, ws, "Equipe");
    }
    XLSX.writeFile(wb, `executivo_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading && !data) return <div className="flex items-center justify-center h-48 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando...</div>;
  if (!data) return <p className="text-sm text-slate-400">Sem dados disponíveis.</p>;

  const { kpis, prev_kpis, pipeline_snapshot, mrr_evolution, team, health_bar, loss_reasons, alerts, top_opportunities, custom_totals, funnel, recent_wins, recent_losses, activity_summary } = data;
  const ps = pipeline_snapshot || {};
  const ct = custom_totals || {};
  const actSum = activity_summary || {};
  const filteredWonDeals = (data.won_deals_table || []).filter(d => wonTableFilter === "all" || d.responsavel === wonTableFilter);

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {alerts?.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium ${
              a.type === "danger" ? "bg-rose-50 text-rose-700 border border-rose-200" :
              a.type === "warning" ? "bg-amber-50 text-amber-700 border border-amber-200" :
              "bg-blue-50 text-blue-700 border border-blue-200"
            }`}>
              {a.type === "danger" ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : a.type === "warning" ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <Info className="w-4 h-4 flex-shrink-0" />}
              {a.message}
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KpiCard icon={DollarSign} title="MRR Ganho" value={kpis.mrr_ganho} unit="R$" sub="no período" color="#10B981" prev={prev_kpis?.mrr_ganho} />
        <KpiCard icon={DollarSign} title="Setup Ganho" value={kpis.setup_ganho} unit="R$" sub="no período" color="#8B5CF6" />
        <KpiCard icon={TrendingUp} title="TCV" value={kpis.tcv_ganho} unit="R$" sub="MRR×12 + Setup" color="#0EA5E9" />
        <KpiCard icon={Trophy} title="Deals Ganhos" value={kpis.deals_ganhos} sub="fechados" color="#10B981" prev={prev_kpis?.deals_ganhos} />
        <KpiCard icon={ThumbsDown} title="Deals Perdidos" value={kpis.deals_perdidos} sub="no período" color="#EF4444" prev={prev_kpis?.deals_perdidos} />
        <KpiCard icon={Target} title="Conversão" value={kpis.taxa_conversao} unit="%" sub="ganhos / total" color={tc500} prev={prev_kpis?.taxa_conversao} />
        <KpiCard icon={DollarSign} title="Ticket Médio" value={kpis.ticket_medio} unit="R$" sub="MRR / deal" color="#6366F1" />
        <KpiCard icon={Clock} title="Tempo Médio" value={kpis.tempo_medio} unit="d" sub="criação → ganho" color="#F59E0B" />
      </div>

      {/* Custom numeric totals (dynamic) */}
      {Object.keys(ct).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(ct).map(([key, info]) => (
            <KpiCard key={key} icon={BarChart2} title={info.label} value={info.type === "currency" ? info.total : info.total.toLocaleString("pt-BR")} unit={info.type === "currency" ? "R$" : ""} sub="negócios ganhos" color="#14B8A6" />
          ))}
        </div>
      )}

      {/* Pipeline Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc500 + "18" }}>
            <BarChart2 className="w-5 h-5" style={{ color: tc500 }} />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">{ps.deals_abertos}</p>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Deals em aberto</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
            <DollarSign className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">{fmtK(ps.mrr_aberto)}</p>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">MRR em aberto</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-50">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">{ps.stagnated_30}</p>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Estagnados 30d+</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-50">
            <Zap className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">{ps.deals_criados}</p>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Criados no período</p>
          </div>
        </div>
      </div>

      {/* Charts Row: MRR Evolution + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" style={{ color: "#10B981" }} />Evolução do MRR Ganho</h3>
          {mrr_evolution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={mrr_evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <RT contentStyle={{ fontSize: 11 }} formatter={v => fmtK(v)} />
                <Line type="monotone" dataKey="mrr" name="MRR" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" style={{ color: tc500 }} />Saúde do Funil</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              {health_bar?.length > 0 && (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={health_bar}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RT contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="count" name="Deals" radius={[4, 4, 0, 0]}>
                      <Cell fill={tc500} /><Cell fill="#10B981" /><Cell fill="#EF4444" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Motivos de Perda</p>
              {loss_reasons?.length > 0 ? (
                <div className="space-y-1.5">
                  {loss_reasons.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[11px] text-slate-700 truncate">{r.reason}</span>
                          <span className="text-[10px] font-semibold text-slate-500 ml-2">{r.count}</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-400 rounded-full" style={{ width: `${Math.round(r.count / Math.max(loss_reasons[0]?.count, 1) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[11px] text-slate-400">Sem perdas no período.</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Team Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4" style={{ color: tc500 }} />Desempenho da Equipe</h3>
          <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600 h-8 text-xs"><Download className="w-3.5 h-3.5" />Exportar</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-y border-slate-100 bg-slate-50/50">
              <tr>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Vendedor</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-right">MRR Ganho</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Ganhos</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Perdidos</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Conversão</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Tarefas</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {team.map((t, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: ["#3B82F6","#10B981","#F59E0B","#EF4444","#8B5CF6"][i % 5] }}>
                        {t.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800">{t.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-700 font-semibold text-right">{fmtK(t.mrr_ganho)}</td>
                  <td className="px-3 py-2.5 text-xs text-emerald-600 font-medium text-center">{t.deals_ganhos}</td>
                  <td className="px-3 py-2.5 text-xs text-rose-500 text-center">{t.deals_perdidos}</td>
                  <td className="px-3 py-2.5 text-xs text-center">
                    <span className={`font-medium ${t.taxa_conversao >= 30 ? "text-emerald-600" : t.taxa_conversao >= 15 ? "text-amber-600" : "text-rose-500"}`}>{t.taxa_conversao}%</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 text-center">{t.tarefas_concluidas}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 text-center">{t.whatsapp}</td>
                </tr>
              ))}
              {team.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-xs text-slate-400">Sem dados da equipe.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Opportunities */}
      {top_opportunities?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" style={{ color: "#8B5CF6" }} />Top 5 Oportunidades em Aberto</h3>
          <div className="space-y-2">
            {top_opportunities.map((o, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 border border-slate-100 rounded-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: ["#3B82F6","#8B5CF6","#F59E0B","#EC4899","#14B8A6"][i] }}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{o.titulo}</p>
                  <p className="text-[10px] text-slate-400">{o.stage} · {o.responsavel}</p>
                </div>
                <span className="text-sm font-bold text-slate-700 flex-shrink-0">{fmtK(o.valor_mrr)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Funnel + Activity Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Funnel by Stage */}
        {funnel?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" style={{ color: tc500 }} />Distribuição por Etapa</h3>
            <ResponsiveContainer width="100%" height={Math.max(funnel.length * 36, 120)}>
              <BarChart data={funnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 10 }} width={100} />
                <RT contentStyle={{ fontSize: 11 }} formatter={(v, name) => name === "mrr" ? fmtK(v) : v} />
                <Bar dataKey="count" name="Deals" fill={tc500} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Activity Summary */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Zap className="w-4 h-4" style={{ color: "#F59E0B" }} />Atividade no Período</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900">{(actSum.total || 0).toLocaleString("pt-BR")}</p>
              <p className="text-[10px] text-slate-400 uppercase font-semibold mt-1">Ações Totais</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900">{(actSum.logins || 0).toLocaleString("pt-BR")}</p>
              <p className="text-[10px] text-slate-400 uppercase font-semibold mt-1">Logins</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Wins & Losses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-500" />Últimos Ganhos</h3>
          {recent_wins?.length > 0 ? (
            <div className="space-y-2">
              {recent_wins.map((d, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 border border-emerald-100 bg-emerald-50/30 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.titulo}</p>
                    <p className="text-[10px] text-slate-400">{d.responsavel} · {d.date ? new Date(d.date).toLocaleDateString("pt-BR") : "—"}</p>
                  </div>
                  <span className="text-xs font-bold text-emerald-700 flex-shrink-0">{fmtK(d.valor_mrr)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">Nenhum ganho no período.</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><ThumbsDown className="w-4 h-4 text-rose-500" />Últimas Perdas</h3>
          {recent_losses?.length > 0 ? (
            <div className="space-y-2">
              {recent_losses.map((d, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 border border-rose-100 bg-rose-50/30 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <ThumbsDown className="w-3.5 h-3.5 text-rose-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.titulo}</p>
                    <p className="text-[10px] text-slate-400">{d.responsavel} · {d.motivo} · {d.date ? new Date(d.date).toLocaleDateString("pt-BR") : "—"}</p>
                  </div>
                  <span className="text-xs font-bold text-rose-600 flex-shrink-0">{fmtK(d.valor_mrr)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">Nenhuma perda no período.</p>}
        </div>
      </div>

      {/* Won Deals Detail Table */}
      {data.won_deals_table?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-500" />Detalhamento dos Ganhos</h3>
            <div className="flex items-center gap-2">
              <Select value={wonTableFilter} onValueChange={setWonTableFilter}>
                <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="Todos os executivos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os executivos</SelectItem>
                  {[...new Set(data.won_deals_table.map(d => d.responsavel))].sort().map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => {
                const rows = filteredWonDeals.map(d => {
                  const row = { "Negócio": d.titulo, "Executivo": d.responsavel, "Setup": d.valor_setup, "MRR": d.valor_mrr, "Data Ganho": d.ganho_em ? new Date(d.ganho_em).toLocaleDateString("pt-BR") : "—" };
                  (data.custom_field_columns || []).forEach(cf => { row[cf.field_label] = (d.custom_fields || {})[cf.field_name] ?? ""; });
                  return row;
                });
                const ws = XLSX.utils.json_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Ganhos");
                XLSX.writeFile(wb, `ganhos_detalhe_${new Date().toISOString().slice(0, 10)}.xlsx`);
              }} className="gap-2 text-slate-600 h-8 text-xs"><Download className="w-3.5 h-3.5" />Exportar</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-y border-slate-100 bg-slate-50/50">
                <tr>
                  <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Negócio</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Executivo</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right">Setup</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right">MRR</th>
                  {(data.custom_field_columns || []).map(cf => (
                    <th key={cf.field_name} className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">{cf.field_label}</th>
                  ))}
                  <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">Data Ganho</th>
                </tr>
              </thead>
              <tbody>
                {filteredWonDeals.map((d, i) => (
                  <tr key={d.id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-xs font-medium text-slate-800 max-w-[200px] truncate">{d.titulo}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{d.responsavel}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 text-right">{fmtK(d.valor_setup)}</td>
                    <td className="px-3 py-2 text-xs text-emerald-700 font-semibold text-right">{fmtK(d.valor_mrr)}</td>
                    {(data.custom_field_columns || []).map(cf => {
                      const val = (d.custom_fields || {})[cf.field_name];
                      return <td key={cf.field_name} className="px-3 py-2 text-xs text-slate-600 text-center">{val != null && val !== "" ? val : "—"}</td>;
                    })}
                    <td className="px-3 py-2 text-[10px] text-slate-400 text-center">{d.ganho_em ? new Date(d.ganho_em).toLocaleDateString("pt-BR") : "—"}</td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="border-t-2 border-slate-200 bg-slate-50/80">
                  <td className="px-3 py-2.5 text-xs font-bold text-slate-800">TOTAL ({filteredWonDeals.length})</td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-xs font-bold text-slate-800 text-right">{fmtK(filteredWonDeals.reduce((s, d) => s + (d.valor_setup || 0), 0))}</td>
                  <td className="px-3 py-2.5 text-xs font-bold text-emerald-700 text-right">{fmtK(filteredWonDeals.reduce((s, d) => s + (d.valor_mrr || 0), 0))}</td>
                  {(data.custom_field_columns || []).map(cf => {
                    if (cf.field_type === "number" || cf.field_type === "currency") {
                      const total = filteredWonDeals.reduce((s, d) => { try { return s + parseFloat((d.custom_fields || {})[cf.field_name] || 0); } catch { return s; } }, 0);
                      return <td key={cf.field_name} className="px-3 py-2.5 text-xs font-bold text-slate-800 text-center">{cf.field_type === "currency" ? fmtK(total) : total.toLocaleString("pt-BR")}</td>;
                    }
                    return <td key={cf.field_name} className="px-3 py-2.5"></td>;
                  })}
                  <td className="px-3 py-2.5"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
